/**
 * A PDF's layout in the save step (SEO_BRIEF_08 section 7.1): With times, the writer's table of
 * number, times and text, or Text only, the words alone (`columns: ['text']`).
 *
 * Built the way the Word layout is (lib/docx.ts): the words are the template's (`ui.pdf_layout`
 * in macros/ui.html), a page mounts it once and calls `sync` wherever its write format changes,
 * and every write asks it for the `formatOptions` to send. A page without the field writes the
 * default table, so every PDF written before this existed is written the same way.
 *
 * The transcript page's "PDF, text only" chip opens /t/{id}/pdf?layout=text, which renders the
 * field on Text only; nothing here reads the URL.
 */

import type { WriteOptions } from '../../../../../contracts/cue-model.ts';
import {
  PDF_DEFAULTS,
  type PdfLayout,
  pdfColumns,
  pdfLayoutOf,
} from '../../../../../src/formats/pdf/options.ts';
import { on, qs, qsa } from './dom.ts';

export type { PdfLayout };
export { pdfLayoutOf };

/** The `formatOptions` a write in `format` sends: the layout for PDF, nothing for the rest. */
export function pdfFormatOptions(
  format: string | undefined,
  layout: PdfLayout,
): WriteOptions['formatOptions'] {
  return format === 'pdf' ? { ...PDF_DEFAULTS, columns: pdfColumns(layout) } : null;
}

export interface PdfLayoutControl {
  /** Show the field while `format` is PDF, hide it otherwise. */
  sync(format: string | undefined): void;
  /** The layout picked right now. */
  layout(): PdfLayout;
  /** The `formatOptions` a write in `format` uses right now. */
  formatOptionsFor(format: string | undefined): WriteOptions['formatOptions'];
}

/**
 * Mount the field under `root`. `onChange` runs when the user picks the other layout, and
 * should write the file again so the button holds that layout.
 */
export function mountPdfLayout(root: ParentNode, onChange: () => void): PdfLayoutControl {
  const field = qs<HTMLElement>('[data-pdf-layout-field]', root);
  const radios = field ? qsa<HTMLInputElement>('[data-pdf-layout]', field) : [];
  for (const radio of radios) {
    on(radio, 'change', () => {
      if (radio.checked) onChange();
    });
  }
  const picked = (): PdfLayout => pdfLayoutOf(radios.find((r) => r.checked)?.value);
  return {
    sync(format) {
      if (field) field.hidden = format !== 'pdf';
    },
    layout: picked,
    formatOptionsFor: (format) => pdfFormatOptions(format, picked()),
  };
}
